import { Input } from "../Forms";
import { useEffect, useRef, useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { AddMarker } from "../../../assets/icons";
import * as React from "react";
import { Dialog } from "primereact/dialog";
import { AddressMap } from "./AddressMap";

interface AddressSearchProps {
  initialValue?: string;
  includeMap?: boolean;
  onLocationSelect?: (location: { lat: number; lng: number; address: string }) => void;
  error?: string;
  handleChange?: (event: { address: string; location: { lat: number; lng: number } }) => void;
  location?: { lat: number; lng: number };
}

function Search({
  initialValue,
  includeMap,
  onLocationSelect,
  error,
  handleChange,
  location,
}: AddressSearchProps) {
  const [searchValue, setSearchValue] = useState("");
  const places = useMapsLibrary("places");

  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const autocompleteService = useRef<google.maps.places.AutocompleteService>();
  const placesService = useRef<google.maps.places.PlacesService>();

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>({
    lat: -26.103147,
    lng: 27.992211,
  });
  const [mapVisible, setMapVisible] = useState<boolean>(false);

  useEffect(() => {
    if (initialValue) {
      setSearchValue(initialValue);
    }
    if (location) {
      setSelectedLocation(location);
    }
  }, [initialValue, location]);

  useEffect(() => {
    if (!places) return;

    autocompleteService.current = new places.AutocompleteService();
    // No map? You can still use PlacesService with a dummy div:
    placesService.current = new places.PlacesService(document.createElement("div"));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setSessionToken(undefined);
  }, [places]);

  useEffect(() => {
    setIsOpen(predictions.length > 0);
    setActiveIndex(-1);
  }, [predictions]);

  const handleInputChange = (value: string) => {
    setSearchValue(value);

    if (!value || !autocompleteService.current || !sessionToken) {
      setPredictions([]);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      { input: value, sessionToken },
      (next, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && next) {
          setPredictions(next.slice(0, 5));
        } else {
          setPredictions([]);
        }
      }
    );
  };

  const fetchPlaceDetails = (placeId: string) => {
    if (!placesService.current || !sessionToken) return;

    placesService.current.getDetails(
      {
        placeId,
        fields: ["geometry", "formatted_address", "address_components", "name", "place_id"],
        sessionToken,
      },
      (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && result?.geometry?.location) {
          const loc = result.geometry.location;
          setSelectedLocation({ lat: loc.lat(), lng: loc.lng() });
          onLocationSelect?.({
            lat: loc.lat(),
            lng: loc.lng(),
            address: result?.formatted_address || "",
          });

          handleChange?.({
            address: result.formatted_address || "",
            location: { lat: loc.lat(), lng: loc.lng() },
          });
          // Optionally update the input with the canonical formatted address
          if (result.formatted_address) setSearchValue(result.formatted_address);
        }
        // End the session after details
        if (places) setSessionToken(new places.AutocompleteSessionToken());
      }
    );
  };

  const selectPrediction = (p: google.maps.places.AutocompletePrediction) => {
    setSearchValue(p.description);
    setPredictions([]);
    setIsOpen(false);
    setActiveIndex(-1);

    // Fetch geometry using the same session token
    fetchPlaceDetails(p.place_id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setIsOpen(true);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, Math.max(0, predictions.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < predictions.length) {
        e.preventDefault();
        selectPrediction(predictions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const listboxId = "address-predictions";

  return (
    <div>
      <div className={"flex"}>
        <label className="block mb-2">Address</label>
        <span className="text-red-500 ml-1">*</span>
      </div>

      <div className={"flex items-center w-full"}>
        {includeMap && (
          <div
            style={{
              background: "#06b6d4",
              height: "50px",
              width: "50px",
              padding: "4px",
              borderBottomLeftRadius: "6px",
              borderTopLeftRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            className={"cursor-pointer"}
            onClick={() => setMapVisible(true)}
          >
            <AddMarker height={28} width={28} fill={"#FFF"} />
          </div>
        )}
        <div style={{ position: "relative", width: "100%" }}>
          <Input
            placeholder="Address search"
            value={searchValue}
            onChange={e => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            fieldClassName={"w-full"}
            error={error}
          />
          {isOpen && predictions.length > 0 && (
            <ul
              id={listboxId}
              role="listbox"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 10000,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderTop: "none",
                borderRadius: "0 0 6px 6px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.07)",
                margin: 0,
                padding: 0,
                listStyle: "none",
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {predictions.map((p, i) => (
                <li
                  id={`address-option-${p.place_id}`}
                  key={p.place_id}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseDown={() => selectPrediction(p)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    background: i === activeIndex ? "#f3f4f6" : "#fff",
                    borderTop: "1px solid #f3f4f6",
                  }}
                >
                  {p.description}
                </li>
              ))}
            </ul>
          )}
        </div>
        {mapVisible && (
          <Dialog
            onHide={() => setMapVisible(false)}
            visible={mapVisible}
            header={"Address search"}
            style={{ width: "50vw" }}
          >
            <AddressMap
              locationGps={`${selectedLocation?.lat.toFixed(6)}, ${selectedLocation?.lng.toFixed(6)}`}
              setLocationGps={val =>
                setSelectedLocation({
                  lat: parseFloat(val.split(",")[0]),
                  lng: parseFloat(val.split(",")[1]),
                })
              }
              locationLabel={searchValue}
              setLocationLabel={setSearchValue}
              setVisible={setMapVisible}
              handleChange={handleChange}
            />
          </Dialog>
        )}
      </div>
    </div>
  );
}

export function AddressSearch(props: AddressSearchProps) {
  return (
    <APIProvider apiKey={"AIzaSyB4m5Hx4i4F8ip8TG9hfuqZCNJ11J05Y_M"}>
      <div className={"w-full"}>
        <Search {...props} />
      </div>
    </APIProvider>
  );
}
